import { useState, useCallback } from "react";
import { useLocation, useParams } from "wouter";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  Plus,
  Building2,
  CreditCard,
  Users,
  Package,
  FileText,
  Settings2,
  Import,
  Eye,
  Pencil,
  X,
  Link,
  Unlink,
  Brain,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import type {
  OnboardingStatus,
  SectionName,
  OnboardingCompanyProfile,
  OnboardingFinancialAccounts,
  OnboardingVendorsClients,
  OnboardingProductsInvoices,
  OnboardingBankAccount,
  OnboardingVendor,
  OnboardingClient,
  OnboardingProduct,
} from "../shared/onboarding-types";
import { formatChf } from "../shared/onboarding-types";
import { cn } from "@/lib/utils";
import { StepIndicator } from "@/components/ui/step-indicator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import confetti from "canvas-confetti";

type Step = "select" | "sources" | "analyzing" | "report" | "import" | "done";

interface SectionConfig {
  key: SectionName;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const SECTIONS: SectionConfig[] = [
  {
    key: "companyProfile",
    title: "Company Profile & Tax",
    icon: <Building2 className="h-5 w-5" />,
    description: "Company name, UID, legal form, address, VAT registration",
  },
  {
    key: "financialAccounts",
    title: "Financial Accounts",
    icon: <CreditCard className="h-5 w-5" />,
    description: "Bank accounts, credit cards, payment processors",
  },
  {
    key: "vendorsAndClients",
    title: "Vendors & Clients",
    icon: <Users className="h-5 w-5" />,
    description: "Suppliers, service providers, customers",
  },
  {
    key: "productsAndInvoices",
    title: "Products & Invoices",
    icon: <Package className="h-5 w-5" />,
    description: "Products purchased, invoices, recurring charges",
  },
];

/**
 * Generate a serialized session name in format: YYYY-MM-DD--HHmm--onboarding-00x
 */
function generateSessionName(sequenceNumber: number): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seq = String(sequenceNumber).padStart(3, "0");

  return `${year}-${month}-${day}--${hours}${minutes}--onboarding-${seq}`;
}

export default function OnboardingWizard() {
  const [, navigate] = useLocation();
  const params = useParams<{ sessionId?: string }>();
  const urlSessionId = params.sessionId ? parseInt(params.sessionId, 10) : null;

  const [step, setStep] = useState<Step>(urlSessionId ? "report" : "select");
  const [sessionName, setSessionName] = useState(() => generateSessionName(1));
  const [sessionId, setSessionId] = useState<number | null>(urlSessionId);
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();

  // Query for existing sessions
  const { data: existingSessions, isLoading: isLoadingSessions } =
    trpc.onboardingWizard.listSessions.useQuery({
      limit: 100, // Get more to calculate next sequence number
    });

  // Update session name with correct sequence number once we know existing count
  const [hasInitializedName, setHasInitializedName] = useState(false);
  if (existingSessions && !hasInitializedName && !urlSessionId) {
    const nextSeq = existingSessions.length + 1;
    setSessionName(generateSessionName(nextSeq));
    setHasInitializedName(true);
  }

  // Query for current session
  const {
    data: session,
    isLoading: isLoadingSession,
    refetch: refetchSession,
  } = trpc.onboardingWizard.getSession.useQuery(
    { sessionId: sessionId! },
    { enabled: sessionId !== null }
  );

  // Query for import preview
  const { data: importPreview, isLoading: isLoadingPreview } =
    trpc.onboardingWizard.previewImport.useQuery(
      { sessionId: sessionId! },
      { enabled: sessionId !== null && step === "import" }
    );

  // Mutations
  const createSession = trpc.onboardingWizard.createSession.useMutation({
    onSuccess: data => {
      setSessionId(data.sessionId);
      setStep("sources");
      setError(null);
    },
    onError: err => {
      setError(err.message);
    },
  });

  const runAnalysis = trpc.onboardingWizard.runAnalysis.useMutation({
    onSuccess: () => {
      setStep("report");
      refetchSession();
    },
    onError: err => {
      setError(err.message);
      setStep("sources");
    },
  });

  const toggleSection = trpc.onboardingWizard.toggleSection.useMutation({
    onSuccess: () => {
      refetchSession();
    },
  });

  const executeImport = trpc.onboardingWizard.executeImport.useMutation({
    onSuccess: () => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      setStep("done");
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    },
    onError: err => {
      setError(err.message);
    },
  });

  const deleteSession = trpc.onboardingWizard.deleteSession.useMutation({
    onSuccess: () => {
      utils.onboardingWizard.listSessions.invalidate();
    },
  });

  const toggleItem = trpc.onboardingWizard.toggleItem.useMutation({
    onSuccess: () => {
      refetchSession();
    },
  });

  const updateAssumption = trpc.onboardingWizard.updateAssumption.useMutation({
    onSuccess: () => {
      refetchSession();
    },
  });

  // Linking mutations
  const addDocuments = trpc.onboardingWizard.addDocuments.useMutation({
    onSuccess: () => {
      refetchSession();
      setDocumentDialogOpen(false);
    },
  });

  const linkIntelligenceSession =
    trpc.onboardingWizard.linkIntelligenceSession.useMutation({
      onSuccess: () => {
        refetchSession();
        setIntelligenceDialogOpen(false);
      },
    });

  const linkDiscoverySession =
    trpc.onboardingWizard.linkDiscoverySession.useMutation({
      onSuccess: () => {
        refetchSession();
        setDiscoveryDialogOpen(false);
      },
    });

  // Unlink mutations
  const unlinkDocument = trpc.onboardingWizard.unlinkDocument.useMutation({
    onSuccess: () => {
      refetchSession();
    },
  });

  const unlinkIntelligenceSession =
    trpc.onboardingWizard.unlinkIntelligenceSession.useMutation({
      onSuccess: () => {
        refetchSession();
      },
    });

  const unlinkDiscoverySession =
    trpc.onboardingWizard.unlinkDiscoverySession.useMutation({
      onSuccess: () => {
        refetchSession();
      },
    });

  // Pagination state
  const ITEMS_PER_PAGE = 10;
  const [vendorPage, setVendorPage] = useState(1);
  const [clientPage, setClientPage] = useState(1);
  const [productPage, setProductPage] = useState(1);

  // Dialog states
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [intelligenceDialogOpen, setIntelligenceDialogOpen] = useState(false);
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);

  // Queries for available data sources (enabled on sources step or when dialogs are open)
  const isOnSourcesStep = step === "sources";

  const { data: availableDocuments, isLoading: isLoadingDocuments } =
    trpc.onboardingWizard.getAvailableDocuments.useQuery(
      { limit: 50 },
      { enabled: (isOnSourcesStep || documentDialogOpen) && sessionId !== null }
    );

  const {
    data: availableIntelligenceSessions,
    isLoading: isLoadingIntelligence,
  } = trpc.onboardingWizard.getAvailableIntelligenceSessions.useQuery(
    { limit: 50 },
    {
      enabled:
        (isOnSourcesStep || intelligenceDialogOpen) && sessionId !== null,
    }
  );

  const { data: availableDiscoverySessions, isLoading: isLoadingDiscovery } =
    trpc.onboardingWizard.getAvailableDiscoverySessions.useQuery(
      { limit: 50 },
      {
        enabled: (isOnSourcesStep || discoveryDialogOpen) && sessionId !== null,
      }
    );

  // State for editing assumptions
  const [editingAssumptionId, setEditingAssumptionId] = useState<string | null>(
    null
  );
  const [editingAssumptionValue, setEditingAssumptionValue] = useState("");

  // Handlers for item exclusion
  const handleToggleItem = (
    itemType: "vendor" | "client" | "account" | "product",
    itemId: string,
    excluded: boolean
  ) => {
    if (!sessionId) return;
    toggleItem.mutate({ sessionId, itemType, itemId, excluded });
  };

  const isItemExcluded = (
    itemType: "vendor" | "client" | "account" | "product",
    itemId: string
  ) => {
    if (!session?.excludedItems) return false;
    switch (itemType) {
      case "vendor":
        return session.excludedItems.vendorIds?.includes(itemId) ?? false;
      case "client":
        return session.excludedItems.clientIds?.includes(itemId) ?? false;
      case "account":
        return session.excludedItems.accountIds?.includes(itemId) ?? false;
      case "product":
        return session.excludedItems.productIds?.includes(itemId) ?? false;
    }
  };

  // Handlers for assumption editing
  const handleStartEditAssumption = (
    assumptionId: string,
    currentValue: string
  ) => {
    setEditingAssumptionId(assumptionId);
    setEditingAssumptionValue(currentValue);
  };

  const handleSaveAssumption = () => {
    if (!sessionId || !editingAssumptionId) return;
    updateAssumption.mutate({
      sessionId,
      assumptionId: editingAssumptionId,
      overrideValue: editingAssumptionValue,
    });
    setEditingAssumptionId(null);
    setEditingAssumptionValue("");
  };

  const handleCancelEditAssumption = () => {
    setEditingAssumptionId(null);
    setEditingAssumptionValue("");
  };

  // ============================================================================
  // Section Content Renderers
  // ============================================================================

  const renderCompanyProfileContent = (
    profile: OnboardingCompanyProfile | undefined
  ) => {
    if (!profile) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          <Settings2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No company profile data available.</p>
        </div>
      );
    }

    return (
      <div className="grid md:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground">
            Basic Information
          </h4>
          <div className="space-y-2">
            {profile.name && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Company Name</span>
                <span className="font-medium">{profile.name}</span>
              </div>
            )}
            {profile.uid && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Swiss UID</span>
                <span className="font-mono">{profile.uid}</span>
              </div>
            )}
            {profile.legalForm && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Legal Form</span>
                <span>{profile.legalForm}</span>
              </div>
            )}
            {profile.industry && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Industry</span>
                <span>{profile.industry}</span>
              </div>
            )}
          </div>
        </div>

        {/* Address */}
        {profile.address && (
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">
              Address
            </h4>
            <div className="space-y-2">
              {profile.address.street && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Street</span>
                  <span>{profile.address.street}</span>
                </div>
              )}
              {(profile.address.zip || profile.address.city) && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">City</span>
                  <span>
                    {profile.address.zip} {profile.address.city}
                  </span>
                </div>
              )}
              {profile.address.canton && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Canton</span>
                  <span>{profile.address.canton}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tax Info */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground">
            Tax Information
          </h4>
          <div className="space-y-2">
            {profile.canton && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax Canton</span>
                <span>{profile.canton}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">VAT Registered</span>
              <Badge variant={profile.vatRegistered ? "default" : "secondary"}>
                {profile.vatRegistered ? "Yes" : "No"}
              </Badge>
            </div>
            {profile.vatNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">VAT Number</span>
                <span className="font-mono">{profile.vatNumber}</span>
              </div>
            )}
            {profile.fiscalYearEnd && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fiscal Year End</span>
                <span>{profile.fiscalYearEnd}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contact */}
        {profile.contact && (
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">
              Contact
            </h4>
            <div className="space-y-2">
              {profile.contact.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{profile.contact.email}</span>
                </div>
              )}
              {profile.contact.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span>{profile.contact.phone}</span>
                </div>
              )}
              {profile.contact.website && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Website</span>
                  <span>{profile.contact.website}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confidence */}
        <div className="md:col-span-2 pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Data Confidence
            </span>
            <Badge variant="outline">{profile.confidence}%</Badge>
          </div>
          {profile.sources && profile.sources.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Sources: {profile.sources.join(", ")}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderFinancialAccountsContent = (
    data: OnboardingFinancialAccounts | undefined
  ) => {
    if (!data || data.accounts.length === 0) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No financial accounts found.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Include</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead>IBAN</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Confidence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.accounts.map(account => (
              <TableRow
                key={account.id}
                className={
                  isItemExcluded("account", account.id) ? "opacity-50" : ""
                }
              >
                <TableCell>
                  <Checkbox
                    checked={!isItemExcluded("account", account.id)}
                    onCheckedChange={checked =>
                      handleToggleItem("account", account.id, !checked)
                    }
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {account.bank || "Unknown"}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {account.iban || "-"}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {account.accountType.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {account.confidence}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-between text-sm text-muted-foreground pt-2 border-t">
          <span>{data.totalAccounts} account(s) found</span>
          <span>Average confidence: {data.confidence}%</span>
        </div>
      </div>
    );
  };

  const renderVendorsClientsContent = (
    data: OnboardingVendorsClients | undefined
  ) => {
    if (!data || (data.vendors.length === 0 && data.clients.length === 0)) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No vendors or clients found.</p>
        </div>
      );
    }

    const vendorTotalPages = Math.ceil(data.vendors.length / ITEMS_PER_PAGE);
    const vendorStartIdx = (vendorPage - 1) * ITEMS_PER_PAGE;
    const vendorEndIdx = vendorStartIdx + ITEMS_PER_PAGE;
    const paginatedVendors = data.vendors.slice(vendorStartIdx, vendorEndIdx);

    const clientTotalPages = Math.ceil(data.clients.length / ITEMS_PER_PAGE);
    const clientStartIdx = (clientPage - 1) * ITEMS_PER_PAGE;
    const clientEndIdx = clientStartIdx + ITEMS_PER_PAGE;
    const paginatedClients = data.clients.slice(clientStartIdx, clientEndIdx);

    return (
      <Tabs defaultValue="vendors" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="vendors">
            Vendors ({data.vendors.length})
          </TabsTrigger>
          <TabsTrigger value="clients">
            Clients ({data.clients.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendors">
          {data.vendors.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No vendors found
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Include</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedVendors.map(vendor => (
                    <TableRow
                      key={vendor.id}
                      className={
                        isItemExcluded("vendor", vendor.id) ? "opacity-50" : ""
                      }
                    >
                      <TableCell>
                        <Checkbox
                          checked={!isItemExcluded("vendor", vendor.id)}
                          onCheckedChange={checked =>
                            handleToggleItem("vendor", vendor.id, !checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{vendor.name}</span>
                          {vendor.uid && (
                            <span className="block text-xs text-muted-foreground font-mono">
                              {vendor.uid}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {vendor.category && (
                          <Badge variant="secondary">{vendor.category}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {vendor.transactionCount}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatChf(Math.abs(vendor.totalAmount))}
                      </TableCell>
                      <TableCell className="text-right">
                        {vendor.confidence}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {vendorTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {vendorStartIdx + 1}-
                    {Math.min(vendorEndIdx, data.vendors.length)} of{" "}
                    {data.vendors.length} vendors
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVendorPage(p => Math.max(1, p - 1))}
                      disabled={vendorPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {vendorPage} of {vendorTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setVendorPage(p => Math.min(vendorTotalPages, p + 1))
                      }
                      disabled={vendorPage === vendorTotalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="clients">
          {data.clients.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No clients found
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Include</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedClients.map(client => (
                    <TableRow
                      key={client.id}
                      className={
                        isItemExcluded("client", client.id) ? "opacity-50" : ""
                      }
                    >
                      <TableCell>
                        <Checkbox
                          checked={!isItemExcluded("client", client.id)}
                          onCheckedChange={checked =>
                            handleToggleItem("client", client.id, !checked)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {client.name}
                      </TableCell>
                      <TableCell>
                        {client.customerType && (
                          <Badge variant="secondary">
                            {client.customerType}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {client.transactionCount}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatChf(client.totalRevenue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {client.confidence}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {clientTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {clientStartIdx + 1}-
                    {Math.min(clientEndIdx, data.clients.length)} of{" "}
                    {data.clients.length} clients
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClientPage(p => Math.max(1, p - 1))}
                      disabled={clientPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {clientPage} of {clientTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setClientPage(p => Math.min(clientTotalPages, p + 1))
                      }
                      disabled={clientPage === clientTotalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    );
  };

  const renderProductsInvoicesContent = (
    data: OnboardingProductsInvoices | undefined
  ) => {
    if (!data || (data.products.length === 0 && data.invoices.length === 0)) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No products or invoices found.</p>
        </div>
      );
    }

    const productTotalPages = Math.ceil(data.products.length / ITEMS_PER_PAGE);
    const productStartIdx = (productPage - 1) * ITEMS_PER_PAGE;
    const productEndIdx = productStartIdx + ITEMS_PER_PAGE;
    const paginatedProducts = data.products.slice(
      productStartIdx,
      productEndIdx
    );

    return (
      <div className="space-y-6">
        {data.products.length > 0 && (
          <div>
            <h4 className="font-medium mb-4">
              Products & Services ({data.products.length})
            </h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Include</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-center">Recurring</TableHead>
                  <TableHead className="text-right">Total Spent</TableHead>
                  <TableHead className="text-right">Confidence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedProducts.map(product => (
                  <TableRow
                    key={product.id}
                    className={
                      isItemExcluded("product", product.id) ? "opacity-50" : ""
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={!isItemExcluded("product", product.id)}
                        onCheckedChange={checked =>
                          handleToggleItem("product", product.id, !checked)
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {product.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{product.type}</Badge>
                    </TableCell>
                    <TableCell>{product.vendorName || "-"}</TableCell>
                    <TableCell className="text-center">
                      {product.isRecurring ? (
                        <Badge variant="default">Yes</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatChf(product.totalSpent)}
                    </TableCell>
                    <TableCell className="text-right">
                      {product.confidence}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {productTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {productStartIdx + 1}-
                  {Math.min(productEndIdx, data.products.length)} of{" "}
                  {data.products.length} products
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProductPage(p => Math.max(1, p - 1))}
                    disabled={productPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {productPage} of {productTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setProductPage(p => Math.min(productTotalPages, p + 1))
                    }
                    disabled={productPage === productTotalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between text-sm text-muted-foreground pt-4 border-t">
          <span>
            {data.totalProducts} product(s), {data.recurringChargesCount}{" "}
            recurring
          </span>
          <span>Confidence: {data.confidence}%</span>
        </div>
      </div>
    );
  };

  const renderSectionContent = (sectionKey: SectionName) => {
    if (isLoadingSession) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    switch (sectionKey) {
      case "companyProfile":
        return renderCompanyProfileContent(session?.companyProfile);
      case "financialAccounts":
        return renderFinancialAccountsContent(session?.financialAccounts);
      case "vendorsAndClients":
        return renderVendorsClientsContent(session?.vendorsAndClients);
      case "productsAndInvoices":
        return renderProductsInvoicesContent(session?.productsAndInvoices);
      default:
        return null;
    }
  };

  // Handlers
  const handleCreateSession = () => {
    if (!sessionName.trim()) {
      setError("Please enter a session name");
      return;
    }
    createSession.mutate({ name: sessionName.trim() });
  };

  const handleSelectSession = (id: number) => {
    setSessionId(id);
    setStep("report");
    navigate(`/onboarding-wizard/${id}`);
  };

  const handleRunAnalysis = () => {
    if (!sessionId) return;
    setStep("analyzing");
    runAnalysis.mutate({ sessionId });
  };

  const handleToggleSection = (section: SectionName, enabled: boolean) => {
    if (!sessionId) return;
    toggleSection.mutate({ sessionId, section, enabled });
  };

  const handleExecuteImport = () => {
    if (!sessionId) return;
    executeImport.mutate({ sessionId });
  };

  // Step indicator config
  const wizardSteps = [
    { label: "Select/Create" },
    { label: "Data Sources" },
    { label: "Review Report" },
    { label: "Import" },
  ];

  const wizardStepIndex = (() => {
    const map: Record<string, number> = {
      select: 0,
      sources: 1,
      analyzing: 1,
      report: 2,
      import: 3,
      done: 3,
    };
    return map[step] ?? 0;
  })();

  // Render Step: Select or Create Session
  const renderSelectStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Session
          </CardTitle>
          <CardDescription>
            Start a new holistic onboarding session to aggregate data from
            multiple sources
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Input
              placeholder="Session name (e.g., 'Q4 2024 Onboarding')"
              value={sessionName}
              onChange={e => setSessionName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreateSession()}
            />
            <Button
              onClick={handleCreateSession}
              disabled={createSession.isPending}
            >
              {createSession.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Create
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoadingSessions ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : existingSessions && existingSessions.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Resume Existing Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {existingSessions.map(s => (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleSelectSession(s.id)}
                >
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        s.status === "completed" ? "default" : "secondary"
                      }
                    >
                      {s.status}
                    </Badge>
                    {s.overallConfidence && (
                      <Badge variant="outline">
                        {s.overallConfidence}% confidence
                      </Badge>
                    )}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );

  // Render Step: Data Sources
  const renderSourcesStep = () => {
    const linkedDocCount = session?.documentIds?.length || 0;
    const linkedIntelCount = session?.intelligenceSessionIds?.length || 0;
    const linkedDiscovery = session?.discoverySessionId;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Data Sources
            </CardTitle>
            <CardDescription>
              Link documents and intelligence sessions to aggregate data from
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                The aggregation engine will analyze linked documents and
                intelligence sessions to extract company profile, financial
                accounts, vendors, clients, and products.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              {/* Documents Section */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Documents</h4>
                  {linkedDocCount > 0 && (
                    <Badge variant="secondary">{linkedDocCount} linked</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Link documents from the File Inbox to include in analysis
                </p>

                {/* Show linked documents */}
                {linkedDocCount > 0 && availableDocuments && (
                  <div className="space-y-2 mb-4">
                    {session?.documentIds?.map(docId => {
                      const doc = availableDocuments.find(d => d.id === docId);
                      return (
                        <div
                          key={docId}
                          className="flex items-center justify-between p-2 bg-primary/5 border border-primary/20 rounded-md"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">
                              {doc?.filename || `Document #${docId}`}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              if (sessionId) {
                                unlinkDocument.mutate({
                                  sessionId,
                                  documentId: docId,
                                });
                              }
                            }}
                            disabled={unlinkDocument.isPending}
                          >
                            {unlinkDocument.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Dialog
                  open={documentDialogOpen}
                  onOpenChange={setDocumentDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Link Documents
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Link Documents</DialogTitle>
                      <DialogDescription>
                        Select documents from your File Inbox to include in
                        analysis
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 mt-4">
                      {isLoadingDocuments ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : availableDocuments &&
                        availableDocuments.length > 0 ? (
                        availableDocuments.map(doc => {
                          const isLinked = session?.documentIds?.includes(
                            doc.id
                          );
                          return (
                            <div
                              key={doc.id}
                              className={cn(
                                "flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors",
                                isLinked
                                  ? "bg-primary/10 border-primary"
                                  : "hover:bg-muted"
                              )}
                              onClick={() => {
                                if (!isLinked && sessionId) {
                                  addDocuments.mutate({
                                    sessionId,
                                    documentIds: [doc.id],
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {doc.filename}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {doc.documentType} •{" "}
                                    {new Date(
                                      doc.uploadedAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              {isLinked ? (
                                <Badge variant="default">Linked</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={addDocuments.isPending}
                                >
                                  {addDocuments.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Link className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-center text-muted-foreground py-8">
                          No documents available. Upload documents in the File
                          Inbox first.
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Intelligence Sessions Section */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Intelligence Sessions</h4>
                  {linkedIntelCount > 0 && (
                    <Badge variant="secondary">{linkedIntelCount} linked</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Link intelligence sessions to import extracted entities
                </p>

                {/* Show linked intelligence sessions */}
                {linkedIntelCount > 0 && availableIntelligenceSessions && (
                  <div className="space-y-2 mb-4">
                    {session?.intelligenceSessionIds?.map(intId => {
                      const intSession = availableIntelligenceSessions.find(
                        s => s.id === intId
                      );
                      return (
                        <div
                          key={intId}
                          className="flex items-center justify-between p-2 bg-primary/5 border border-primary/20 rounded-md"
                        >
                          <div className="flex items-center gap-2">
                            <Brain className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">
                              {intSession?.name ||
                                `Intelligence Session #${intId}`}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              if (sessionId) {
                                unlinkIntelligenceSession.mutate({
                                  sessionId,
                                  intelligenceSessionId: intId,
                                });
                              }
                            }}
                            disabled={unlinkIntelligenceSession.isPending}
                          >
                            {unlinkIntelligenceSession.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Dialog
                  open={intelligenceDialogOpen}
                  onOpenChange={setIntelligenceDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Link Intelligence Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Link Intelligence Session</DialogTitle>
                      <DialogDescription>
                        Select an intelligence session to import extracted
                        vendors, clients, and entities
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 mt-4">
                      {isLoadingIntelligence ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : availableIntelligenceSessions &&
                        availableIntelligenceSessions.length > 0 ? (
                        availableIntelligenceSessions.map(sess => {
                          const isLinked =
                            session?.intelligenceSessionIds?.includes(sess.id);
                          return (
                            <div
                              key={sess.id}
                              className={cn(
                                "flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors",
                                isLinked
                                  ? "bg-primary/10 border-primary"
                                  : "hover:bg-muted"
                              )}
                              onClick={() => {
                                if (!isLinked && sessionId) {
                                  linkIntelligenceSession.mutate({
                                    sessionId,
                                    intelligenceSessionId: sess.id,
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <Brain className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {sess.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {sess.status} •{" "}
                                    {new Date(
                                      sess.createdAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              {isLinked ? (
                                <Badge variant="default">Linked</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={linkIntelligenceSession.isPending}
                                >
                                  {linkIntelligenceSession.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Link className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-center text-muted-foreground py-8">
                          No intelligence sessions available. Create one in the
                          Intelligence Workspace first.
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Discovery Session Section */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Discovery Session</h4>
                  {linkedDiscovery && (
                    <Badge variant="secondary">1 linked</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Link a company discovery session for company profile data
                </p>

                {/* Show linked discovery session */}
                {linkedDiscovery && availableDiscoverySessions && (
                  <div className="space-y-2 mb-4">
                    {(() => {
                      const discSession = availableDiscoverySessions.find(
                        s => s.id === linkedDiscovery
                      );
                      return (
                        <div className="flex items-center justify-between p-2 bg-primary/5 border border-primary/20 rounded-md">
                          <div className="flex items-center gap-2">
                            <Search className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">
                              {discSession?.projectName ||
                                `Discovery Session #${linkedDiscovery}`}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              if (sessionId) {
                                unlinkDiscoverySession.mutate({ sessionId });
                              }
                            }}
                            disabled={unlinkDiscoverySession.isPending}
                          >
                            {unlinkDiscoverySession.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <Dialog
                  open={discoveryDialogOpen}
                  onOpenChange={setDiscoveryDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Link Discovery Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Link Discovery Session</DialogTitle>
                      <DialogDescription>
                        Select a company discovery session to import company
                        profile and bank accounts
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 mt-4">
                      {isLoadingDiscovery ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : availableDiscoverySessions &&
                        availableDiscoverySessions.length > 0 ? (
                        availableDiscoverySessions.map(sess => {
                          const isLinked =
                            session?.discoverySessionId === sess.id;
                          return (
                            <div
                              key={sess.id}
                              className={cn(
                                "flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors",
                                isLinked
                                  ? "bg-primary/10 border-primary"
                                  : "hover:bg-muted"
                              )}
                              onClick={() => {
                                if (!isLinked && sessionId) {
                                  linkDiscoverySession.mutate({
                                    sessionId,
                                    discoverySessionId: sess.id,
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <Search className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {sess.projectName}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {sess.status} •{" "}
                                    {new Date(
                                      sess.createdAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              {isLinked ? (
                                <Badge variant="default">Linked</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={linkDiscoverySession.isPending}
                                >
                                  {linkDiscoverySession.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Link className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-center text-muted-foreground py-8">
                          No discovery sessions available. Create one in Company
                          Discovery first.
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep("select")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleRunAnalysis} disabled={runAnalysis.isPending}>
            {runAnalysis.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Run Analysis
          </Button>
        </div>
      </div>
    );
  };

  // Render Step: Analyzing
  const renderAnalyzingStep = () => (
    <div className="flex flex-col items-center justify-center py-16 space-y-4">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <h3 className="text-xl font-semibold">Analyzing Data Sources</h3>
      <p className="text-muted-foreground text-center max-w-md">
        Aggregating data from documents, intelligence sessions, and discovery
        sessions to build your onboarding report...
      </p>
    </div>
  );

  // Render Step: Report Review
  const renderReportStep = () => (
    <div className="space-y-6">
      <Tabs defaultValue="companyProfile" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {SECTIONS.map(section => (
            <TabsTrigger
              key={section.key}
              value={section.key}
              className="flex items-center gap-2"
            >
              {section.icon}
              <span className="hidden lg:inline">
                {section.title.split(" ")[0]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {SECTIONS.map(section => (
          <TabsContent key={section.key} value={section.key}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <div>
                      <CardTitle>{section.title}</CardTitle>
                      <CardDescription>{section.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Include in import
                    </span>
                    <Switch
                      checked={session?.enabledSections?.[section.key] ?? true}
                      onCheckedChange={checked =>
                        handleToggleSection(section.key, checked)
                      }
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>{renderSectionContent(section.key)}</CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Assumptions Panel */}
      {session?.assumptions && session.assumptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Assumptions
            </CardTitle>
            <CardDescription>
              The system made the following assumptions based on the data. Click
              to edit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {session.assumptions.map((assumption: any) => (
                <div
                  key={assumption.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium">{assumption.fieldLabel}</p>
                    <p className="text-sm text-muted-foreground">
                      {assumption.reason}
                    </p>
                    {assumption.overridden && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Original: {assumption.assumedValue}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editingAssumptionId === assumption.id ? (
                      <>
                        <Input
                          value={editingAssumptionValue}
                          onChange={e =>
                            setEditingAssumptionValue(e.target.value)
                          }
                          className="w-32 h-8"
                          onKeyDown={e => {
                            if (e.key === "Enter") handleSaveAssumption();
                            if (e.key === "Escape")
                              handleCancelEditAssumption();
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleSaveAssumption}
                          disabled={updateAssumption.isPending}
                        >
                          {updateAssumption.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEditAssumption}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge
                          variant={
                            assumption.overridden ? "default" : "outline"
                          }
                          className="cursor-pointer"
                          onClick={() =>
                            handleStartEditAssumption(
                              assumption.id,
                              assumption.overridden
                                ? assumption.overrideValue
                                : assumption.assumedValue
                            )
                          }
                        >
                          {assumption.overridden
                            ? assumption.overrideValue
                            : assumption.assumedValue}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() =>
                            handleStartEditAssumption(
                              assumption.id,
                              assumption.overridden
                                ? assumption.overrideValue
                                : assumption.assumedValue
                            )
                          }
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep("sources")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sources
        </Button>
        <Button onClick={() => setStep("import")}>
          <Eye className="h-4 w-4 mr-2" />
          Preview Import
        </Button>
      </div>
    </div>
  );

  // Render Step: Import Preview
  const renderImportStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Import className="h-5 w-5" />
            Import Preview
          </CardTitle>
          <CardDescription>
            Review what will be created when you execute the import
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingPreview ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : importPreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-4 border rounded-lg text-center">
                  <Building2 className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">
                    {importPreview.companyWillBeCreated ? 1 : 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Company</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <CreditCard className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">
                    {importPreview.bankAccountsToCreate}
                  </p>
                  <p className="text-sm text-muted-foreground">Bank Accounts</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <Users className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">
                    {importPreview.vendorsToCreate}
                  </p>
                  <p className="text-sm text-muted-foreground">Vendors</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <Users className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">
                    {importPreview.clientsToCreate}
                  </p>
                  <p className="text-sm text-muted-foreground">Clients</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <Package className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">
                    {importPreview.productsToCreate}
                  </p>
                  <p className="text-sm text-muted-foreground">Products</p>
                </div>
              </div>

              {importPreview.warnings && importPreview.warnings.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <ul className="list-disc list-inside">
                      {importPreview.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No preview data available
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep("report")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Report
        </Button>
        <Button
          onClick={handleExecuteImport}
          disabled={executeImport.isPending}
        >
          {executeImport.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Import className="h-4 w-4 mr-2" />
          )}
          Execute Import
        </Button>
      </div>
    </div>
  );

  // Render Step: Done
  const renderDoneStep = () => (
    <div className="flex flex-col items-center justify-center py-16 space-y-4">
      <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center">
        <Check className="h-8 w-8 text-success" />
      </div>
      <h3 className="text-xl font-semibold">Import Complete!</h3>
      <p className="text-muted-foreground text-center max-w-md">
        Your data has been successfully imported. Redirecting to dashboard...
      </p>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-primary" />
            Holistic Onboarding Wizard
          </h1>
          <p className="text-muted-foreground mt-2">
            Aggregate data from multiple sources to build a comprehensive
            onboarding report
          </p>
        </div>

        <StepIndicator steps={wizardSteps} currentStep={wizardStepIndex} />

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === "select" && renderSelectStep()}
        {step === "sources" && renderSourcesStep()}
        {step === "analyzing" && renderAnalyzingStep()}
        {step === "report" && renderReportStep()}
        {step === "import" && renderImportStep()}
        {step === "done" && renderDoneStep()}
      </div>
    </DashboardLayout>
  );
}
